import React, { useCallback, useState } from 'react'

import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material'
import { Button, useNotify, useTranslate } from 'react-admin'

import { LXDatePeriod } from '../../components/LXDatePeriod'
import { API } from '../../helpers/Api.helper'
import { BeneficiaryHelper } from '../../helpers/Beneficiary.helper'
import { DateHelper } from '../../helpers/Date.helper'
import genericStyles from './../../generics.module.scss'
import styles from './CreateStatementModal.module.scss'

export interface CreateStatementModalProps {
  beneficiaryId?: string
  type?: 'beneficiary' | 'allBeneficiaries' | 'statementsSummaryCSV'
  isOpen?: boolean
  onClose: (created: boolean) => void
}

export const CreateStatementModal: React.FC<CreateStatementModalProps> = ({
  beneficiaryId,
  type = 'beneficiary',
  isOpen = false,
  onClose,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [creating, setCreating] = React.useState(false)
  const [month, setMonth] = useState<number | undefined>(DateHelper.currentDate().get('M'))
  const [year, setYear] = useState<number | undefined>(DateHelper.currentDate().get('y'))
  const [error, setError] = useState<string>()

  React.useEffect(() => {
    if (!isOpen) {
      setMonth(DateHelper.currentDate().get('M'))
      setYear(DateHelper.currentDate().get('y'))
      setCreating(false)
    }
  }, [isOpen])

  const closeModal = useCallback(
    (isCreated: boolean) => {
      onClose(isCreated)
      setError(undefined)
    },
    [onClose, setError],
  )

  const createStatement = React.useCallback(async () => {
    if (type === 'beneficiary' && !beneficiaryId) {
      throw new Error('Should have beneficiaryId or shouldTargetAllBeneficiaries=true')
    }
    setCreating(true)
    setError(undefined)
    try {
      if (type === 'statementsSummaryCSV') {
        if (!beneficiaryId) {
          throw new Error('Should have beneficiaryId')
        }

        if (!month || !year) {
          throw new Error('Date is not defined')
        }

        await BeneficiaryHelper.downloadStatementsSummaryCsv({
          start: { month, year },
          end: { month, year },
          beneficiariesIds: [beneficiaryId],
        })
      } else {
        const url = type === 'allBeneficiaries' ? '/api/statements/job-all' : '/api/statements/job'
        const job = (await API.post(url, { beneficiaryId, month, year })).data.job
        // await the job to be done
        const fetchJobStatus = async (): Promise<any> => {
          await new Promise(resolve => setTimeout(resolve, 1000))
          const up = (await API.get<any>(`/api/statements/jobStatus/${job.id}`)).data?.job
          if (up?.finishedOn) {
            return up
          }
          return fetchJobStatus()
        }
        const finishedJob = await fetchJobStatus()
        if (finishedJob.returnvalue.length === 0) {
          throw new Error('No statements created')
        }
      }
      closeModal(true)
      setCreating(false)
    } catch (e: any) {
      const message = e.response?.data?.message ?? e.response

      notify(`${message}`, { type: 'error' })
      console.error('Error - create statement', e)
      setError(`${message} - ${e.response?.data.content?.fieldError ?? e.response?.data.content}`)
    }
  }, [beneficiaryId, notify, closeModal, type, month, year])

  return (
    <Dialog aria-labelledby="youtube-import-modal" open={isOpen} onClose={() => closeModal(false)}>
      <DialogTitle id="youtube-import-modal">
        {translate(
          type === 'statementsSummaryCSV'
            ? 'pages.statements.createSummary'
            : type === 'allBeneficiaries'
              ? 'pages.statements.createAll'
              : 'pages.beneficiaries.createStatement',
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction="row" spacing={4}>
          <Grid item lg={12}>
            <LXDatePeriod
              month={month?.toString()}
              year={year?.toString()}
              onUpdate={(month: string | undefined, year: string | undefined): void => {
                if (month) {
                  setMonth(parseInt(month))
                }
                if (year) {
                  setYear(parseInt(year))
                }
              }}
            />
          </Grid>
        </Grid>
        {creating && !error && (
          <div className={styles.message}>
            <CircularProgress size="1rem" />
          </div>
        )}
        {error && (
          <p className={genericStyles.error}>
            <i>{translate('_generics.errorOccured')}</i>:&nbsp;
            <strong>{error}</strong>
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeModal(false)}
          label={translate('_generics.cancel')}
          disabled={creating && !error}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            createStatement()
          }}
          label={translate('_generics.create')}
          disabled={creating || !month || !year}
        />
      </DialogActions>
    </Dialog>
  )
}
